import React, { type ReactNode, useMemo } from "react";
import Layout from "../Layout/Layout";
import DashboardSideBar from "../Layout/DashboardSideBar/DashboardSideBar";
import DashboardHeader from "../Layout/DashboardSideBar/DashboardHeader";
import PrizesConfigWrapper from "./components/GiftsConfiguration/PrizesConfigWrapper";
import PrizesAndLoyaltyConfigWrapper from "./components/GiftsConfiguration/PrizesAndLoyaltyConfigWrapper";
import {
  baseSteps,
  STEP_1,
  STEP_2,
  STEP_3,
  STEP_4,
  STEP_5,
  STEP_6,
  STEP_7
} from "../03_Onboarding/helpers/constants";
import { type Step } from "../../components/Stepper/Stepper";
import CTAWrapper from "./components/CallToAction/CTAWrapper";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import {
  configAnimation,
  configAnimationWithTracking
} from "../../utils/routes/routes";
import {
  buildPathWithTrackingId,
  useTrackingIdRouting
} from "../../utils/routing/routingUtils";
import GamesConfigurator from "./components/GameTypeSelector/GamesConfigurator";
import LogoAndColorsEditor from "./components/LogoAndColorEditor/LogoAndColorsEditor";
import POSAGallery from "./components/POSDisplay/POSAGallery";
import POSAList from "./components/POSDisplay/component/POSAList";
import InfoDelivering from "./components/POSDisplay/component/InfoDelivering";
import MLContainer from "./components/MLSelector/MLContainer";
import JetpackMarketingAutomationWrapper from "./components/MarketingAutomation/JetpackMarketingAutomationWrapper";
import { useAccountToDisplaySelector } from "../../redux/actions/user";
import { MobileHeader } from "../Layout/Mobile/MobileHeader";
import LoyaltyGiftsConfigWrapper, {
  LoyaltyTiersGiftsEditor,
  LoyaltyLeaderboardGiftsEditor
} from "./components/GiftsConfiguration/LoyaltyGiftsConfigWrapper";
import { filterStepsByFeatures } from "../../utils/helpers/filterStepsByFeatures";
import Settings from "./components/Settings/Settings";
import { useSubscriptionPlansSelector } from "../../redux/actions/app";

export default function FullScreenConfigurator() {
  const history = useHistory();
  useTrackingIdRouting("FullScreenConfigurator"); // Handle trackingID from URL
  const { accountToDisplay } = useAccountToDisplaySelector();

  const onClickItem = (step: Step) => {
    if (!step.pathName) {
      return;
    }
    history.push(
      buildPathWithTrackingId(
        `/configuration/${step.pathName}`,
        accountToDisplay?.trackingID
      )
    );
  };

  const renderConfiguration = (step: Step): ReactNode => {
    switch (step.index) {
      case STEP_1:
        return <LogoAndColorsEditor />;
      case STEP_2:
        return <PrizesAndLoyaltyConfigWrapper />;
      case STEP_3:
        return <GamesConfigurator />;
      case STEP_4:
        return <CTAWrapper />;
      case STEP_5:
        return <JetpackMarketingAutomationWrapper />;
      case STEP_6:
        return <POSAGallery />;
      case STEP_7:
        return <Settings />;
      default:
        return <div> Erreur</div>;
    }
  };

  const plans = useSubscriptionPlansSelector();

  const plan = useMemo(() => {
    return plans?.find((p) => p._id === accountToDisplay?.saasPlanId) ?? null;
  }, [accountToDisplay]);

  const BASE_STEPS = useMemo(() => {
    return baseSteps.filter((step) =>
      filterStepsByFeatures(step, accountToDisplay?.saasFeatures, plan?.price)
    );
  }, [accountToDisplay, plan]);

  return (
    <Layout
      showSidebar={true}
      sideBar={
        <DashboardSideBar onClickItem={onClickItem} steps={BASE_STEPS} />
      }
      showHeader={true}
      header={
        <>
          <MobileHeader />
          <div className="d-flex align-items-center justify-content-between w-100">
            <DashboardHeader />
          </div>
        </>
      }
    >
      <Switch>
        {/* Routes with trackingID */}
        <Route
          path={`${configAnimationWithTracking}/distribution/quantity/info`}
        >
          <InfoDelivering />
        </Route>
        <Route path={`${configAnimationWithTracking}/distribution/quantity`}>
          <POSAList gotoPath={history.push} />
        </Route>
        <Route path={`${configAnimationWithTracking}/prizes/setting`}>
          <PrizesConfigWrapper />
        </Route>
        <Route path={`${configAnimationWithTracking}/prizes/gifts/tiers`}>
          <LoyaltyGiftsConfigWrapper ChildComponent={LoyaltyTiersGiftsEditor} />
        </Route>
        <Route path={`${configAnimationWithTracking}/prizes/gifts/leaderboard`}>
          <LoyaltyGiftsConfigWrapper
            ChildComponent={LoyaltyLeaderboardGiftsEditor}
          />
        </Route>
        <Route path={`${configAnimationWithTracking}/prizes/glp`}>
          <MLContainer />
        </Route>
        {BASE_STEPS.map((step) => {
          return (
            <Route
              key={`tracking-${step.name}`}
              path={`${configAnimationWithTracking}/${step.pathName}`}
            >
              {renderConfiguration(step)}
            </Route>
          );
        })}

        {/* Routes without trackingID (for backward compatibility) */}
        <Route path={`${configAnimation}/distribution/quantity/info`}>
          <InfoDelivering />
        </Route>
        <Route path={`${configAnimation}/distribution/quantity`}>
          <POSAList gotoPath={history.push} />
        </Route>
        <Route path={`${configAnimation}/prizes/setting`}>
          <PrizesConfigWrapper />
        </Route>
        <Route path={`${configAnimation}/prizes/loyalty`}>
          <LoyaltyGiftsConfigWrapper />
        </Route>
        <Route path={`${configAnimation}/prizes/gifts/tiers`}>
          <LoyaltyGiftsConfigWrapper ChildComponent={LoyaltyTiersGiftsEditor} />
        </Route>
        <Route path={`${configAnimation}/prizes/gifts/leaderboard`}>
          <LoyaltyGiftsConfigWrapper
            ChildComponent={LoyaltyLeaderboardGiftsEditor}
          />
        </Route>
        <Route exact path={`${configAnimation}/prizes/gifts`}>
          <LoyaltyGiftsConfigWrapper />
        </Route>
        <Route path={`${configAnimation}/prizes/glp`}>
          <MLContainer />
        </Route>
        <Route path={`${configAnimation}/loyalty`}>
          <Redirect to="/configuration/prizes" />
        </Route>
        {baseSteps
          .filter((step) =>
            filterStepsByFeatures(step, accountToDisplay?.saasFeatures)
          )
          .map((step) => {
            return (
              <Route
                key={step.name}
                path={`${configAnimation}/${step.pathName}`}
              >
                {renderConfiguration(step)}
              </Route>
            );
          })}
      </Switch>
    </Layout>
  );
}
